import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useBodyTemperatureCalculations,
  useBodyTemperatureChartConfig,
  useBodyTemperatureChartData,
  useBodyTemperatureData,
} from "~/hooks";
import { TimeRangeSelector } from "../Shared";
import { BodyTemperatureBreakdown } from "./BodyTemperatureBreakdown";
import { BodyTemperatureChart } from "./BodyTemperatureChart";
import { BodyTemperatureSummary } from "./BodyTemperatureSummary";

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface BodyTemperatureDetailViewProps {
  patientId: number;
  patientName: string;
}

export const BodyTemperatureDetailView: React.FC<
  BodyTemperatureDetailViewProps
> = ({ patientId, patientName }) => {
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState<TimeRange>("7d");
  const [navigationOffset, setNavigationOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Fetch body temperature data from API
  const {
    data: apiData,
    metadata,
    loading,
    error,
  } = useBodyTemperatureData({
    patientId,
    timeRange: selectedRange,
    navigationOffset,
    selectedDate,
  });

  // Transform API data to chart format using custom hook
  const { filteredData, chartData } = useBodyTemperatureChartData({
    apiData,
    selectedRange,
  });

  // Get chart configuration using custom hook
  const chartConfig = useBodyTemperatureChartConfig({
    selectedRange,
    navigationOffset,
    lowChartData: chartData,
    selectedDate,
  });

  // Use the calculations hook
  const { timeContext: _timeContext, breakdownTitle } =
    useBodyTemperatureCalculations({
      filteredData,
      timeRange: selectedRange,
      navigationOffset,
    });

  // Handle time range change and reset to current period
  const handleTimeRangeChange = (newRange: TimeRange) => {
    setSelectedRange(newRange);
    setNavigationOffset(0); // Reset to current period
    setSelectedDate(new Date()); // Reset to current date
  };

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    // Reset navigation offset since we're now using selectedDate directly
    setNavigationOffset(0);
  };

  // Navigation functions
  const handlePrevious = () => {
    const newDate = new Date(selectedDate);

    switch (selectedRange) {
      case "1d":
        newDate.setDate(newDate.getDate() - 1); // Previous day
        break;
      case "7d":
        newDate.setDate(newDate.getDate() - 7); // Previous week
        break;
      case "1m":
        newDate.setMonth(newDate.getMonth() - 1); // Previous month
        break;
      case "6m":
        newDate.setMonth(newDate.getMonth() - 6); // Previous 6 months
        break;
      case "1y":
        newDate.setFullYear(newDate.getFullYear() - 1); // Previous year
        break;
      default:
        newDate.setDate(newDate.getDate() - 1);
    }

    setSelectedDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(selectedDate);

    switch (selectedRange) {
      case "1d":
        newDate.setDate(newDate.getDate() + 1); // Next day
        break;
      case "7d":
        newDate.setDate(newDate.getDate() + 7); // Next week
        break;
      case "1m":
        newDate.setMonth(newDate.getMonth() + 1); // Next month
        break;
      case "6m":
        newDate.setMonth(newDate.getMonth() + 6); // Next 6 months
        break;
      case "1y":
        newDate.setFullYear(newDate.getFullYear() + 1); // Next year
        break;
      default:
        newDate.setDate(newDate.getDate() + 1);
    }

    setSelectedDate(newDate);
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between border-b border-gray-700 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-lg font-medium text-blue-400 transition-colors hover:text-blue-300"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-semibold text-white">
          Body Temperature - {patientName}
        </h1>
        <div className="w-16"></div> {/* Spacer for centering */}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Time Range Selector */}
        <TimeRangeSelector
          selectedRange={selectedRange}
          setSelectedRange={handleTimeRangeChange}
        />

        {/* Summary Statistics */}
        <BodyTemperatureSummary
          filteredData={filteredData}
          metadata={metadata}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          onDateSelect={handleDateSelect}
          selectedDate={selectedDate}
          selectedRange={selectedRange}
        />

        {/* Chart */}
        <div className="mx-auto w-4/5 rounded-xl bg-gray-900/50 p-4">
          <BodyTemperatureChart
            key={`${selectedRange}-${selectedDate.getTime()}-${filteredData.length}`}
            selectedRange={selectedRange}
            navigationOffset={navigationOffset}
            filteredData={filteredData}
            chartData={chartData}
            loading={loading}
            error={error}
            metadata={metadata}
            chartConfig={chartConfig}
          />
        </div>

        {/* Breakdown List */}
        <BodyTemperatureBreakdown
          selectedRange={selectedRange}
          filteredData={filteredData}
          breakdownTitle={breakdownTitle}
          loading={loading}
          error={error}
        />
      </div>
    </div>
  );
};
